/* ─────────────────────────────────────────────────────────────
   FINAL VERDICT — a trader's verdict from the signals the page already has.
   v3 (14 Oct 2026, after Roberto's review). Line-for-line mirror of the bot's
   verdict.py; test_verdict.py there diffs the two on a shared scenario set.

   1. CONVICTION  quality = FA rating (0.70) + valuation (0.30), where valuation is
      ONE score: the equal blend of the true-value (DCF) gap and the analyst-target
      gap after a 12% haircut, each on its own continuous ladder. Fair value is not
      used — it is the mean of those two anchors and would count the DCF twice.
      TV model-error gate: gap < −50% or > +100%, LOW confidence, an open-exceptions
      ticker, or a stale forecast → TV score capped ±1, "valuation unreliable",
      confidence Low.
   2. TIMING      trend (price vs 50/200-DMA, ties count as below, 0.5% tolerance)
      + one mild ramped RSI rule (±0.5 at 75/25) + the 1-month move (±0.5);
      momentum clipped ±1 before it joins trend; timing clipped ±2.
   3. RISK        the monthly implied move is the STRADDLE move → ÷1.25 to a 1-SD
      move → four size steps. A print inside the expiry used blocks a NEW position
      either way (Buy or Sell → "Hold · wait for the print").
   Verdict = matrix on conviction × timing, then the vetoes: quality Sell or worse
   never Buy; quality gate failed (FA profitability / balance-sheet factor D or
   worse) never Strong Buy; strongly negative valuation caps at Buy (momentum row →
   Hold); a momentum Buy needs quality ≥ Hold, valuation > −1 and no TV gate; a
   momentum "reduce" needs quality ≤ Hold and valuation < +1. Missing data: no
   chart → capped at Hold; no valuation → no Strong labels; no FA → no verdict; no
   implied move → size "unknown". Confidence: three views with a dead band, trend
   agreeing with an A/B FA-momentum factor is not independent evidence, missing
   input / TV gate / completeness < 1 cap it. Hold carries a sub-label.

   Every number lives in DEFAULT_WEIGHTS; src/config/verdictWeights.json (a copy
   of the bot's fitted data/verdict_weights.json) overrides it key by key.
───────────────────────────────────────────────────────────── */

export const VERSION = "v3-2026-10-14";

export const DEFAULT_WEIGHTS = {
  conviction: { quality: 0.7, valuation: 0.3, call_tone: 0.0 },
  timing: { news: 0.0 },
  valuation: { tv_weight: 0.5, analyst_weight: 0.5, tv_weight_low_conf: 0.25, analyst_haircut: 0.12,
               tv_mild: 15, tv_strong: 40, an_mild: 10, an_strong: 25,
               gate_low: -50, gate_high: 100, gate_cap: 1.0, strong_negative: -1.5 },
  timing_rules: { rsi_hi: 75, rsi_hi_from: 65, rsi_lo: 25, rsi_lo_from: 35, rsi_step: 0.5, chase_1m: 15, reset_1m: -15, trend_tol_pct: 0.5 },
  risk: { straddle_to_sd: 1.25, size_full: 6.0, size_normal: 9.0, size_half: 13.0 },
  matrix: { conv_strong: 1.5, conv_act: 1.0, timing_act: 1.0, timing_momentum: 1.5, tol: 0.05 },
  confidence: { dead_band: 0.25 },
};
let fitted = {};
let exceptions = [];
try { fitted = require("../config/verdictWeights.json").weights || {}; } catch (e) { fitted = {}; }
try { exceptions = require("../config/tvExceptions.json").tickers || []; } catch (e) { exceptions = []; }
export const mergeWeights = (...layers) => {
  const out = JSON.parse(JSON.stringify(DEFAULT_WEIGHTS));
  for (const layer of layers) for (const k of Object.keys(layer || {})) if (out[k] && typeof layer[k] === "object") Object.assign(out[k], layer[k]);
  return out;
};
export const WEIGHTS = mergeWeights(fitted);
export const TV_EXCEPTIONS = new Set(exceptions.map((t) => String(t).toUpperCase()));

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const r2 = (v) => (v ? Math.sign(v) * Math.floor(Math.abs(v) * 100 + 0.5 + 1e-9) / 100 : 0);
const ramp = (v, strong, mild) => { const a = Math.abs(v); if (a <= mild) return 0; const s = a >= strong ? 2 : (2 * (a - mild)) / (strong - mild); return r2(v > 0 ? s : -s); };
const pct = (v, dp = 0) => `${v >= 0 ? "+" : ""}${Number(v).toFixed(dp)}%`;
const money = (v) => (v == null ? "—" : Number(v).toFixed(2));
const js = (v) => String(v);
const RATING_SCORE = [[/strong\s*buy/i, 2], [/strong\s*sell/i, -2], [/buy/i, 1], [/sell/i, -1], [/hold|neutral/i, 0]];
const WEAK = new Set(["D", "E", "F"]);
const grade = (factors, key) => { const g = (factors || {})[`${key}_grade`]; return g ? String(g).trim().toUpperCase().slice(0, 1) : null; };
const daysUntil = (s, today) => { if (!s) return null; const d = new Date(String(s).slice(0, 10) + "T00:00:00"); if (Number.isNaN(d.getTime())) return null; const t0 = today ? new Date(today + "T00:00:00") : new Date(new Date().toDateString()); return Math.round((d - t0) / 86400000); };
export const LABELS = ["Strong Sell", "Sell", "Hold", "Buy", "Strong Buy"];
const ORDER = { "Strong Sell": -2, Sell: -1, Hold: 0, Buy: 1, "Strong Buy": 2 };

/* ── 1. conviction ─────────────────────────────────────────── */
function conviction(summary, factors, ticker, W) {
  const Wc = W.conviction, V = W.valuation;
  const parts = [], flags = [];
  const fa = summary.fa_grade || {};
  const rating = String(fa.rating || "");
  const hit = RATING_SCORE.find(([re]) => re.test(rating));
  let quality = null;
  if (hit) {
    quality = hit[1];
    parts.push({ name: "Quality (FA rating)", score: quality, weight: Wc.quality, tab: "ratings",
                 reason: `FA grade ${fa.grade || "—"} · ${rating}${num(fa.score) !== null ? ` (score ${Number(fa.score).toFixed(1)})` : ""}` });
  }
  const iv = summary.intrinsic_value || {};
  const ivu = num(iv.upside_downside_pct);
  const ivOk = ivu !== null && !iv.declined_reason;
  const ivLow = /low/i.test(String(iv.confidence || ""));
  const at = summary.analyst_target || {};
  const atu = num(at.upside_pct);
  const atOk = atu !== null;
  let tvGated = false;
  const gateWhy = [];
  if (ivOk) {
    if (ivu < V.gate_low) gateWhy.push(`gap ${pct(ivu)} beyond ${V.gate_low.toFixed(0)}%`);
    if (ivu > V.gate_high) gateWhy.push(`gap ${pct(ivu)} beyond +${V.gate_high.toFixed(0)}%`);
    if (ivLow) gateWhy.push("LOW confidence");
    if (ticker && TV_EXCEPTIONS.has(String(ticker).toUpperCase())) gateWhy.push("on the open-exceptions list");
    if (iv.stale) gateWhy.push("forecast older than the last reported quarter");
    tvGated = gateWhy.length > 0;
  }
  if (ivOk || atOk) {
    let tvS = ivOk ? ramp(ivu, V.tv_strong, V.tv_mild) : null;
    if (tvS !== null && tvGated) tvS = r2(clamp(tvS, -V.gate_cap, V.gate_cap));
    const anAdj = atOk ? (1 + atu / 100) * (1 - V.analyst_haircut) * 100 - 100 : null;
    const anS = atOk ? ramp(anAdj, V.an_strong, V.an_mild) : null;
    const wTv = ivOk ? (ivLow ? V.tv_weight_low_conf : V.tv_weight) : 0;
    const wAn = atOk ? V.analyst_weight : 0;
    const val = ((tvS || 0) * wTv + (anS || 0) * wAn) / (wTv + wAn);
    const bits = [];
    if (ivOk) bits.push(`true value ${money(iv.iv_per_share)} = ${pct(ivu)} vs price` + (tvGated ? ` (capped ±1: ${gateWhy.join(", ")})` : ""));
    if (atOk) { const n = at.num_analysts_3m; bits.push(`analyst target ${money(at.target_price)} = ${pct(atu)}, ${pct(anAdj)} after the 12% haircut` + (n ? ` (${n} analysts)` : "")); }
    parts.push({ name: "Valuation (true value + analyst target)", score: r2(val), weight: Wc.valuation, tab: "true-value", reason: bits.join("; ") });
    if (tvGated) flags.push("valuation unreliable — model disagreement, investigate");
  }
  const es = summary.earnings_sentiment || {};
  const tone = num(es.composite_score);
  if (parts.length && (Wc.call_tone || 0) > 0 && tone !== null) {
    const sc = r2(clamp((tone - 50) / 25, -2, 2));
    const q = es.fiscal_quarter ? `Q${es.fiscal_quarter} FY${es.fiscal_year}` : "last call";
    parts.push({ name: "Call tone", score: sc, weight: Wc.call_tone, tab: "earnings-review", reason: `management tone ${tone.toFixed(1)} (${es.band || "—"}) on the ${q}` });
  }
  if (quality === null) return { score: null, parts, quality: null, valuation: null, tv_gated: tvGated, flags, quality_gate: null };
  const wsum = parts.reduce((a, p) => a + p.weight, 0);
  const score = parts.reduce((a, p) => a + p.score * p.weight, 0) / wsum;
  const valuation = (parts.find((p) => p.name.startsWith("Valuation")) || {}).score ?? null;
  const weak = ["profitability", "balance_sheet_quality"].filter((k) => WEAK.has(grade(factors, k)));
  const quality_gate = { failed: weak.length > 0, why: weak.length ? weak.map((k) => `${k.replace(/_/g, " ")} ${grade(factors, k)}`).join(", ") : null, engine_quality_ok: summary._quality_ok };
  return { score: r2(clamp(score, -2, 2)), parts, quality, valuation, tv_gated: tvGated, flags, quality_gate };
}

/* ── 2. timing ─────────────────────────────────────────────── */
function timing(fv, summary, W) {
  const T = W.timing_rules;
  const pts = (fv.prices || []).filter((p) => p && num(p.close) !== null);
  if (pts.length < 30) return { score: null, parts: [], tech: null };
  const closes = pts.map((p) => Number(p.close));
  const last = pts[pts.length - 1];
  const px = closes[closes.length - 1];
  const d50 = num(last.dma50), d200 = num(last.dma200);
  const r1m = closes.length > 21 ? (px / closes[closes.length - 22] - 1) * 100 : null;
  const rsi = num(fv.rsi14);
  const parts = [];
  const tol = T.trend_tol_pct / 100;
  const above = (a, b) => a > b * (1 + tol);
  let trend = 0, why = "trend unreadable";
  if (d50 && d200) {
    const upPx = above(px, d50), up50 = above(d50, d200);
    if (upPx && up50) { trend = 2; why = `uptrend: price ${money(px)} > 50-DMA ${money(d50)} > 200-DMA ${money(d200)}`; }
    else if (!upPx && !up50 && !above(px, d200)) { trend = -2; why = `downtrend: price ${money(px)} ≤ 50-DMA ${money(d50)} ≤ 200-DMA ${money(d200)}`; }
    else if (!upPx && up50 && above(px, d200)) { trend = 1; why = `pullback inside an uptrend: price ${money(px)} at or below the 50-DMA ${money(d50)}, above the 200-DMA ${money(d200)}`; }
    else if (upPx && !up50 && !above(px, d200)) { trend = -1; why = `bounce inside a downtrend: price ${money(px)} above the 50-DMA ${money(d50)}, at or below the 200-DMA ${money(d200)}`; }
    else { trend = 0; why = `mixed trend: price ${money(px)}, 50-DMA ${money(d50)}, 200-DMA ${money(d200)}`; }
  }
  parts.push({ name: "Trend", score: trend, tab: "fairvalue", reason: why });
  let mom = 0; const bits = [];
  if (rsi !== null) {
    if (rsi > T.rsi_hi_from) { mom += r2(-T.rsi_step * Math.min(1, (rsi - T.rsi_hi_from) / (T.rsi_hi - T.rsi_hi_from))); bits.push(`RSI ${rsi.toFixed(0)} — ${rsi >= T.rsi_hi ? "stretched" : "getting stretched"}`); }
    else if (rsi < T.rsi_lo_from) { mom += r2(T.rsi_step * Math.min(1, (T.rsi_lo_from - rsi) / (T.rsi_lo_from - T.rsi_lo))); bits.push(`RSI ${rsi.toFixed(0)} — ${rsi <= T.rsi_lo ? "washed out" : "getting washed out"}`); }
    else bits.push(`RSI ${rsi.toFixed(0)} — neutral`);
  } else bits.push("RSI unavailable");
  if (r1m !== null) {
    if (r1m >= T.chase_1m) { mom -= 0.5; bits.push(`up ${r1m.toFixed(1)}% in a month — chasing`); }
    else if (r1m <= T.reset_1m && trend >= 0) { mom += 0.5; bits.push(`down ${Math.abs(r1m).toFixed(1)}% in a month inside an uptrend — a reset`); }
    else bits.push(`${pct(r1m, 1)} in a month`);
  }
  mom = r2(clamp(mom, -1, 1));
  parts.push({ name: "Momentum", score: mom, tab: "momentum", reason: bits.join("; ") });
  let score = trend + mom;
  const ns = (summary || {}).news_sentiment || {};
  const nsc = num(ns.composite_score);
  const wn = W.timing.news || 0;
  if (wn > 0 && nsc !== null && !ns.quiet) {
    const nSc = r2(clamp((nsc - 50) / 25, -2, 2));
    parts.push({ name: "News", score: nSc, tab: "news-sentiment", reason: `press sentiment ${nsc.toFixed(1)} (${ns.band || "—"}) this week` });
    score = (score * 1.0 + nSc * wn) / (1.0 + wn);
  }
  return { score: r2(clamp(score, -2, 2)), parts, tech: { px, d50, d200, rsi, r1m } };
}

/* ── 3. risk ───────────────────────────────────────────────── */
function risk(summary, review, today, W) {
  const R = W.risk;
  const mo = (summary.implied_volatility || {}).monthly || {};
  const straddle = num(mo.implied_move);
  const out = { size: "unknown", sizeWhy: "no listed options — size on your own volatility view", event: null, band: null, expiry: mo.expiry || null, move_1sd: null };
  if (straddle !== null) {
    const sd = straddle / R.straddle_to_sd;
    out.move_straddle = straddle; out.move_1sd = r2(sd);
    out.size = sd < R.size_full ? "full" : sd < R.size_normal ? "normal" : sd < R.size_half ? "half" : "quarter";
    out.sizeWhy = `1-SD monthly move ±${sd.toFixed(1)}% (straddle ±${straddle.toFixed(1)}%) → ${out.size} size (full < ${R.size_full.toFixed(0)}%, normal < ${R.size_normal.toFixed(0)}%, half < ${R.size_half.toFixed(0)}%, else quarter)`;
    if (mo.band_low != null && mo.band_high != null) out.band = [Number(mo.band_low), Number(mo.band_high)];
  }
  const ne = (review || {}).next_earnings || {};
  const days = daysUntil(ne.report_date, today);
  const expDays = daysUntil(mo.expiry, today);
  const horizon = expDays !== null && expDays > 0 ? expDays : 30;
  if (days !== null && days >= 0 && days <= horizon) {
    const d = String(ne.report_date).slice(0, 10);
    out.event = { days, date: d, reason: `earnings in ${days} day${days === 1 ? "" : "s"} (${d}), inside the expiry used — no new position either way; let the market reprice first` };
  }
  return out;
}

/* ── the matrix ────────────────────────────────────────────── */
export function decide(c, t, W = WEIGHTS) {
  const M = W.matrix, tol = M.tol;
  if (c === null) return [null, null, null];
  if (t === null) t = 0;
  if (c >= M.conv_act - tol) {
    if (t >= M.timing_act - tol) return [c >= M.conv_strong - tol ? "Strong Buy" : "Buy", null, "buy — quality and price agree and the trend confirms"];
    if (t >= -tol) return ["Buy", null, "buy — the case is there; the chart neither confirms nor refuses"];
    return ["Hold", "accumulate", "accumulate on weakness — the case is there but the trend is against you; scale in at the levels below rather than today"];
  }
  if (c <= -M.conv_act + tol) {
    if (t <= -M.timing_act + tol) return [c <= -M.conv_strong + tol ? "Strong Sell" : "Sell", null, "sell — weak case and the chart agrees"];
    if (t <= tol) return ["Sell", null, "sell — the case is poor and the chart gives no reason to wait"];
    return ["Hold", "trim", "trim into strength — expensive or weak, but trending; do not fight the tape, sell the rallies"];
  }
  if (t >= M.timing_momentum - tol) return ["Buy", "momentum", "momentum trade, no valuation support — smaller size; the trend is doing the work"];
  if (t <= -M.timing_momentum + tol) return ["Sell", "momentum", "reduce — nothing to hold it up and the trend is down"];
  return ["Hold", "no edge", "no edge — neither the case nor the chart is strong enough to act"];
}
const capAt = (label, at) => (ORDER[label] > ORDER[at] ? at : label);

/**
 * @param summary   /summary payload
 * @param fairValue /fair-value-chart payload (prices, rsi14, entry/exit, quality_ok)
 * @param review    /earnings-review payload (next_earnings)
 * @param factors   /factor-ratings payload (the six *_grade fields)
 * @param ticker    for the open-exceptions list
 * @param opts      { today: "YYYY-MM-DD", weights: override }
 */
export function computeFinalRating(summary, fairValue, review, factors, ticker, opts = {}) {
  const W = opts.weights ? mergeWeights(fitted, opts.weights) : WEIGHTS;
  const today = opts.today || null;
  const s = { ...(summary || {}) };
  const fv = fairValue || {};
  s._quality_ok = fv.quality_ok;
  const V = W.valuation;
  const C = conviction(s, factors, ticker, W);
  const T = timing(fv, s, W);
  const R = risk(s, review, today, W);
  const notes = [...(C.flags || [])];
  if (C.score === null) return { label: null, sublabel: null, insufficient: true, conviction: C, timing: T, risk: R, version: VERSION, reason: "No FA grade for this name — no verdict." };

  let [label, sub, action] = decide(C.score, T.score, W);
  const q = C.quality, val = C.valuation;
  if (q !== null && q <= -1 && ORDER[label] > 0) { label = "Hold"; sub = (T.score || 0) > 0 ? "trim" : "no edge"; action = "quality is Sell or worse — never a Buy, whatever the chart"; notes.push("quality veto"); }
  if (C.quality_gate && C.quality_gate.failed && label === "Strong Buy") { label = "Buy"; notes.push(`quality gate (${C.quality_gate.why}) — never Strong Buy`); }
  if (val !== null && val <= V.strong_negative && ORDER[label] > 0) {
    if (sub === "momentum") { label = "Hold"; sub = "momentum only"; action = "uptrend, but strongly overvalued on both anchors — no new position; let the trend prove it"; }
    else label = capAt(label, "Buy");
    notes.push("strongly negative valuation caps the Buy side");
  }
  if (sub === "momentum" && label === "Buy" && (q === null || q < 0 || val === null || val <= -1 || C.tv_gated)) {
    label = "Hold"; sub = "momentum only";
    action = C.tv_gated ? "uptrend, but the valuation input is unreliable — no new position on momentum alone" : "uptrend without a fundamental case — no new position on momentum alone";
  }
  if (sub === "momentum" && label === "Sell" && (q === null || q > 0 || val === null || val >= 1)) { label = "Hold"; sub = "no edge"; action = "downtrend, but the fundamentals do not support selling — hold, do not add"; }

  const items = { fa: q !== null, valuation: val !== null, chart: T.score !== null, implied_move: R.move_1sd !== null };
  const completeness = r2(Object.values(items).filter(Boolean).length / Object.keys(items).length);
  if (T.score === null && ORDER[label] > 0) { label = "Hold"; sub = "timing unreadable"; action = "the case supports a buy, but there is no chart series to time it — hold until there is"; }
  if (T.score === null && ORDER[label] < 0) { label = "Hold"; sub = "timing unreadable"; action = "the case is weak, but there is no chart series to time a sale — hold, do not add"; }
  if (val === null && (label === "Strong Buy" || label === "Strong Sell")) { label = label === "Strong Buy" ? "Buy" : "Sell"; notes.push("no valuation input — no Strong labels"); }

  let eventNote = null;
  if (R.event && label !== "Hold") { eventNote = R.event.reason; const prior = label; label = "Hold"; sub = "wait for print"; action = `wait for the print — the case reads ${prior}, but ${R.event.reason}`; }
  if (sub === "momentum" && label === "Buy" && R.size === "full") { R.size = "normal"; R.sizeWhy = (R.sizeWhy || "") + " — capped at normal: momentum-only"; }

  const db = W.confidence.dead_band;
  const views = { quality: q, valuation: val, timing: T.score };
  const held = Object.entries(views).filter(([, v]) => v !== null && Math.abs(v) >= db);
  const signs = held.map(([k, v]) => [k, v > 0 ? 1 : -1]);
  const disagree = new Set(signs.map(([, s]) => s)).size > 1;
  const momGrade = grade(factors, "momentum");
  let agreeing = signs.map(([k]) => k);
  if (agreeing.length === 2 && agreeing.includes("quality") && agreeing.includes("timing") && (momGrade === "A" || momGrade === "B")) agreeing = ["quality"];
  let confidence = disagree ? "Low" : agreeing.length >= 2 ? "High" : "Medium";
  if (Object.values(views).some((v) => v === null) && confidence === "High") confidence = "Medium";
  if (C.tv_gated) confidence = "Low";
  if (completeness < 1 && confidence === "High") confidence = "Medium";

  const levels = [];
  if (num(fv.entry_price) !== null) levels.push({ name: "Fair-value entry", value: Number(fv.entry_price) });
  if (num(fv.exit_price) !== null) levels.push({ name: "Fair-value exit", value: Number(fv.exit_price) });
  if (T.tech && T.tech.d50) levels.push({ name: "50-DMA", value: T.tech.d50 });
  if (T.tech && T.tech.d200) levels.push({ name: "200-DMA", value: T.tech.d200 });
  if (R.band) levels.push({ name: `Options band to ${R.expiry ? String(R.expiry).slice(0, 10) : "month end"}`, value: R.band });

  const cWord = C.score >= 1 ? "strong" : C.score > 0 ? "mildly positive" : C.score <= -1 ? "weak" : C.score < 0 ? "mildly negative" : "neutral";
  const tWord = T.score === null ? "not readable (no chart series)" : T.score >= 1 ? "supportive" : T.score <= -1 ? "against you" : "neutral";
  const shown = label + (sub && label === "Hold" ? ` · ${sub}` : "");
  const reason = `${shown} (${confidence.toLowerCase()} confidence). The case is ${cWord} (conviction ${C.score >= 0 ? "+" : ""}${js(C.score)}): `
    + C.parts.map((p) => p.reason).join("; ") + ". "
    + `Timing is ${tWord}${T.score !== null ? ` (${T.score >= 0 ? "+" : ""}${js(T.score)})` : ""}`
    + (T.parts.length ? `: ${T.parts.map((p) => p.reason).join("; ")}` : "") + ". "
    + `Action: ${action}.` + (R.sizeWhy ? ` Size: ${R.sizeWhy}.` : "") + (notes.length ? ` Notes: ${notes.join("; ")}.` : "");
  return { label, sublabel: sub, action, confidence, insufficient: false, conviction: C, timing: T, risk: R, levels, eventNote, notes, completeness, reason, version: VERSION };
}

export default computeFinalRating;
